<template>
    <div class="d-flex flex-column justify-space-between h-100">
        <LoadingOverlay :model-value="submitting" />
        <v-card-title>{{ $t("addTransportIncident.title") }}</v-card-title>
        <v-card-text>
            <v-form ref="form" @submit.prevent="submitIncident">
                <IncidentCompanySelect
                    v-model="incident.memberId"
                    :rules="requiredRule"
                />
                <v-textarea
                    data-cy="incident-description"
                    v-model="incident.description"
                    :label="$t('addTransportIncident.description')"
                    rows="3"
                    required
                    :rules="requiredRule"
                />
                <v-date-input
                    data-cy="incident-date"
                    v-model="incident.incident_date"
                    :label="$t('addTransportIncident.incidentDate')"
                    full-width
                    variant="outlined"
                    :rules="requiredRule"
                />
                <p class="text-body-2 mb-2">
                    {{ $t("addTransportIncident.documentsNote") }}
                </p>
                <IncidentFileInput
                    v-model="incident.documents"
                    :minFiles="1"
                    :label="$t('addTransportIncident.documentsLabel')"
                    accept=".pdf"
                    clearable
                />
            </v-form>
        </v-card-text>
    </div>
</template>

<script>
import axios from "axios";
import { mapMutations } from "vuex";
import emitter from "@/eventBus";
import { showAlertModal } from "@/utils";
import IncidentCompanySelect from "@/components/forms/IncidentCompanySelect.vue";
import IncidentFileInput from "@/components/forms/IncidentFileInput.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import dayjs from "dayjs";

export default {
    name: "AddTransportIncident",
    components: {
        IncidentCompanySelect,
        IncidentFileInput,
        LoadingOverlay,
    },
    data() {
        return {
            submitting: false,
            incident: {
                memberId: "",
                description: "",
                incident_date: null,
                documents: [],
            },
        };
    },
    mounted() {
        emitter.on("language-changed", this.revalidateForm);
        this.SET_FORM_BUTTONS([
            {
                label: "common.save", // i18n key
                event: "save-truck-incident",
                color: "primary",
                dataCy: 'transport-incident-save',
            },
        ]);
        emitter.on("save-truck-incident", this.submitIncident);
    },
    beforeUnmount() {
        emitter.off("language-changed", this.revalidateForm);
        emitter.off("save-truck-incident", this.submitIncident);
        this.RESET_FORM_BUTTONS();
    },
    computed: {
        requiredRule() {
            return [(v) => !!v || this.$t("validation.required")];
        },
    },
    methods: {
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        showAlertModal,
        revalidateForm() {
            this.$refs.form?.validate();
        },
        async submitIncident() {
            const formResult = await this.$refs.form.validate();

            if (!formResult?.valid) {
                return;
            }
            this.submitting = true;
            try {
                const formData = new FormData();
                formData.append("companyId", this.incident.memberId);
                formData.append("description", this.incident.description);
                formData.append(
                    "incident_date",
                    dayjs(this.incident.incident_date).format("YYYY-MM-DD")
                );

                // append each doc
                for (const file of this.incident.documents) {
                    formData.append("documents", file);
                }

                await axios.post(
                    `${process.env.VUE_APP_API_URL}/transport-incidents/`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                this.showAlertModal(
                    this.$store,
                    "Transport Incident created successfully!",
                    "success",
                    5000
                );
                this.$refs.form.reset();
                this.$router.push("/dashboard/my-incidents");
            } catch (error) {
                console.error("Error submitting incident:", error);
                // Check for 404 from get_object_or_404
                if (error.response && error.response.status === 404) {
                    this.showAlertModal(
                        this.$store,
                        this.$t("validation.companyNotFound"),
                        "danger",
                        12000
                    );
                } else {
                    this.showAlertModal(
                        this.$store,
                        this.$t("validation.incidentError"),
                        "danger",
                        12000
                    );
                }
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
